import { Component, Input, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
  FormBuilder,
  FormGroup,
  Validators,
  ReactiveFormsModule,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { of } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { AlgorithmService } from '../../services/algorithm.service';
import { ToastsService } from '../../lib/services/toasts.service';
import {
  AlgorithmFeed,
  AlgorithmParameter,
} from '../../models/algorithm.model';

/**
 * Editable percentage form for a single discovery feed. All parameters are
 * shown as percentage inputs that must sum to 100. Direction (boost/penalty)
 * is fixed server-side; the admin only sets relative weight.
 */
@Component({
  selector: 'sa-algorithm-feed-form',
  templateUrl: './algorithm-feed-form.component.html',
  styleUrls: ['./algorithm-feed-form.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatProgressSpinnerModule,
  ],
})
export class AlgorithmFeedFormComponent implements OnInit {
  @Input() feed!: AlgorithmFeed;

  form: FormGroup = this.fb.group({});
  parameters: AlgorithmParameter[] = [];
  loading = true;
  saving = false;
  resetting = false;

  constructor(
    private fb: FormBuilder,
    private algorithmService: AlgorithmService,
    private toasts: ToastsService,
  ) {}

  ngOnInit(): void {
    this.load();
  }

  /** Validator: the sum of all percentage controls must equal 100 (+/- 0.5). */
  private sumTo100Validator = (group: AbstractControl): ValidationErrors | null => {
    const total = this.currentTotal(group as FormGroup);
    return Math.abs(total - 100) <= 0.5 ? null : { sumNot100: { total } };
  };

  currentTotal(group: FormGroup = this.form): number {
    return Object.values(group.controls).reduce(
      (sum, ctrl) => sum + (Number(ctrl.value) || 0),
      0,
    );
  }

  get totalRounded(): number {
    return Math.round(this.currentTotal() * 100) / 100;
  }

  get isBalanced(): boolean {
    return Math.abs(this.currentTotal() - 100) <= 0.5;
  }

  private load(): void {
    this.loading = true;
    this.algorithmService.getSettings(this.feed).pipe(
      tap((settings) => {
        this.parameters = settings.parameters;
        const controls: { [key: string]: any } = {};
        settings.parameters.forEach((p) => {
          controls[p.key] = [
            Math.round(p.percent * 100) / 100,
            [Validators.required, Validators.min(0), Validators.max(100)],
          ];
        });
        this.form = this.fb.group(controls, { validators: this.sumTo100Validator });
        this.loading = false;
      }),
      catchError((error) => {
        this.toasts.showDanger(`Failed to load algorithm settings: ${JSON.stringify(error.error || error.message)}`);
        this.loading = false;
        return of(null);
      }),
    ).subscribe();
  }

  /** Fill the form with default percentages locally (preview, not saved). */
  fillDefaults(): void {
    this.parameters.forEach((p) => {
      this.form.get(p.key)?.setValue(Math.round(p.defaultPercent * 100) / 100);
    });
    this.form.updateValueAndValidity();
  }

  /**
   * Reset to defaults IN THE DATABASE: removes the stored override so the
   * server reverts to its built-in default weights, then reloads the form.
   */
  resetToDefaults(): void {
    this.resetting = true;
    this.algorithmService.resetSettings(this.feed).pipe(
      tap((settings) => {
        this.parameters = settings.parameters;
        const controls: { [key: string]: any } = {};
        settings.parameters.forEach((p) => {
          controls[p.key] = [
            Math.round(p.percent * 100) / 100,
            [Validators.required, Validators.min(0), Validators.max(100)],
          ];
        });
        this.form = this.fb.group(controls, { validators: this.sumTo100Validator });
        this.resetting = false;
        this.toasts.showSuccess('Reset to default weights.');
      }),
      catchError((error) => {
        this.toasts.showDanger(`Reset failed: ${JSON.stringify(error.error || error.message)}`);
        this.resetting = false;
        return of(null);
      }),
    ).subscribe();
  }

  /** Normalize current values so they sum to exactly 100, preserving ratios. */
  normalize(): void {
    const total = this.currentTotal();
    if (total <= 0) { return; }
    const scale = 100 / total;
    this.parameters.forEach((p) => {
      const ctrl = this.form.get(p.key);
      const scaled = Math.round((Number(ctrl?.value) || 0) * scale * 100) / 100;
      ctrl?.setValue(scaled);
    });
    this.form.updateValueAndValidity();
  }

  save(): void {
    if (this.form.invalid) {
      this.toasts.showDanger('Percentages must each be 0-100 and sum to 100.');
      return;
    }
    this.saving = true;
    const percents = this.form.value as { [key: string]: number };
    this.algorithmService.updateSettings(this.feed, { percents }).pipe(
      tap(() => {
        this.toasts.showSuccess('Algorithm settings saved.');
        this.saving = false;
      }),
      catchError((error) => {
        this.toasts.showDanger(`Save failed: ${JSON.stringify(error.error || error.message)}`);
        this.saving = false;
        return of(null);
      }),
    ).subscribe();
  }
}
